/**
 * quantity-input.js — <quantity-input> Web Component
 * Wraps a number input with +/- buttons. Enforces min/max. Dispatches quantity:change.
 */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this._input = this.querySelector('.qty-input') || this.querySelector('input[type="number"]');
    if (!this._input) return;

    this._min = parseInt(this.getAttribute('min') ?? this._input.min ?? '0', 10);
    this._max = parseInt(this.getAttribute('max') ?? this._input.max ?? '9999', 10);

    this.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', this._handleButtonClick.bind(this));
    });

    this._input.addEventListener('change', this._handleInputChange.bind(this));
    this._input.addEventListener('input', this._validateInput.bind(this));

    this._updateButtonStates();
  }

  _handleButtonClick(event) {
    const action = event.currentTarget.dataset.action;
    const current = parseInt(this._input.value, 10) || 0;
    const previous = current;
    let next = action === 'increment' ? current + 1 : current - 1;
    next = Math.max(this._min, Math.min(this._max, next));
    if (next === current) return;

    this._input.value = next;
    this._updateButtonStates();
    this._dispatch(next, previous);
  }

  _handleInputChange() {
    const previous = parseInt(this._input.dataset.previous ?? this._input.value, 10) || 0;
    const value = this._clamp(parseInt(this._input.value, 10) || this._min);
    this._input.value = value;
    this._updateButtonStates();
    if (value !== previous) this._dispatch(value, previous);
  }

  _validateInput() {
    const value = parseInt(this._input.value, 10);
    if (!isNaN(value)) this._input.dataset.previous = value;
  }

  _clamp(value) {
    return Math.max(this._min, Math.min(this._max, value));
  }

  _updateButtonStates() {
    const value = parseInt(this._input.value, 10) || 0;
    this.querySelectorAll('[data-action="decrement"]').forEach((btn) => {
      btn.disabled = value <= this._min;
    });
    this.querySelectorAll('[data-action="increment"]').forEach((btn) => {
      btn.disabled = value >= this._max;
    });
  }

  _dispatch(value, previousValue) {
    this.dispatchEvent(new CustomEvent(Summit.events.QUANTITY_CHANGE, {
      bubbles: true,
      detail: { value, previousValue, input: this._input },
    }));
    document.dispatchEvent(new CustomEvent(Summit.events.QUANTITY_CHANGE, {
      bubbles: true,
      detail: { value, previousValue, input: this._input },
    }));
  }
}

customElements.define('quantity-input', QuantityInput);
